'use client'

import keys from 'lodash/keys'
import {Box, CircleX} from 'lucide-react'
import {observer} from 'mobx-react'
import {useSearchParams} from 'next/navigation'
import {useRouter} from 'nextjs-toploader/app'
import React, {useEffect} from 'react'

import {useQueryClient} from '@tanstack/react-query'

import {Routes} from '@/src/config/routes'

import {BasketPageContent, useBasket} from '@/src/entities/basket'

import * as OrdersUI from '@/src/(pages)/profile/ui/orders.styled'

import noPhotoPic from '@/src/shared/assets/media/no-photo.png'
import {Image} from '@/src/shared/ui/image'
import {
  InputCount,
  InputMinus,
  InputPlus,
  PlusMinusInput,
} from '@/src/shared/ui/inputs/plus-minus-input'
import {NextLink} from '@/src/shared/ui/next-link'

import * as UI from './page.screen.styled'
import {getInjection} from '@runlab/di'
import {BasketEventTypes} from '@/src/core/domain/events'
import {PageData} from '@/src/core/domain/entities/page/page-data'
import {ProductsListContainer} from '@/src/features/catalog/product-list/products-list-container'
import {Alert, Tooltip} from 'antd'
import {AsyncProcessLoading} from '@/src/shared/ui/layout/ui/async-loading'

export const BasketScreen: React.FC<PageData<BasketPageContent>> = observer(props => {
  const router = useRouter()
  const basket = useBasket()
  const queryClient = useQueryClient()

  const searchParams = useSearchParams()

  useEffect(() => {
    if (searchParams.get('refresh')) {
      queryClient.invalidateQueries({queryKey: ['layout']})
    }

    const subscription = getInjection('IEventBus').subscribe(BasketEventTypes.BasketSynced, () => {
      router.refresh()
    })

    return subscription.unsubscribe
  }, [])

  if (basket.loading && !basket.basketData) return <AsyncProcessLoading />

  const goBuy = () => {
    router.push(Routes.order.page)
  }

  return (
    <>
      <UI.BasketContent data-testid="basket-items">
        {(keys(basket.basketData?.lines).length > 0 && (
          <>
            <UI.ProductList>
              {keys(basket.basketData?.lines).map(basketItemKey => {
                const basketItem = basket.basketData!.lines[basketItemKey]!

                return (
                  <UI.ProductRow
                    key={basketItemKey}
                    data-testid="basket-item"
                    data-any-cart-item-id={basketItem.variant_id ?? basketItem.product_id}
                    data-available={(basketItem.available && '+') || '-'}>
                    <OrdersUI.ProductImage>
                      <Image
                        src={
                          (basketItem.product.images.length > 0 &&
                            `${basketItem.product.images[0]}`) ||
                          noPhotoPic
                        }
                        alt={basketItem.product.name || ''}
                        sizes="100vw"
                        width={181}
                        height={104}
                      />
                    </OrdersUI.ProductImage>

                    <UI.ProductContent>
                      <UI.ProductName
                        as={NextLink}
                        href={Routes.catalog.product.page(
                          basketItem.product.code,
                          basketItem.product.type,
                        )}
                        data-any-cart-item-name={basketItem.name}>
                        {basketItem.name}
                      </UI.ProductName>
                    </UI.ProductContent>

                    <UI.Prices>
                      Цена:{' '}
                      {(basketItem.price !== basketItem.discount_price && (
                        <del>{basketItem.price_formatted}</del>
                      )) || (
                        <span data-any-cart-item-price={basketItem.discount_price}>
                          {basketItem.price_formatted}
                        </span>
                      )}
                      {basketItem.price !== basketItem.discount_price && (
                        <span data-any-cart-item-price={basketItem.discount_price}>
                          {basketItem.discount_price}
                        </span>
                      )}
                    </UI.Prices>

                    <UI.ProductChangeCount>
                      <PlusMinusInput>
                        <InputMinus
                          $disabled={basket.pending || !basketItem.available}
                          onClick={() => basket.decreaseCount(basketItem)}>
                          -
                        </InputMinus>
                        <InputCount
                          $disabled={false}
                          data-any-cart-item-quantity={
                            (basket.modifyItems ?? {})[basketItem.id]
                              ? basket.modifyItems[basketItem.id] &&
                                basket.modifyItems[basketItem.id]!.quantity
                              : basketItem.quantity
                          }>
                          {(basket.modifyItems ?? {})[basketItem.id]
                            ? basket.modifyItems[basketItem.id] &&
                              basket.modifyItems[basketItem.id]!.quantity
                            : basketItem.quantity}
                        </InputCount>
                        <InputPlus
                          $disabled={basket.pending || !basketItem.available}
                          onClick={() => basket.increaseCount(basketItem)}>
                          +
                        </InputPlus>
                      </PlusMinusInput>
                    </UI.ProductChangeCount>

                    <UI.ProductPrice>{basketItem.discount_total_formatted}</UI.ProductPrice>

                    <UI.RemoveItem onClick={() => basket.deleteBasketItem(basketItem)}>
                      {!basketItem.available && (
                        <Tooltip placement="bottomRight" title="Товара нет в наличии" showArrow>
                          <Box color="orange" />
                        </Tooltip>
                      )}

                      <CircleX />
                    </UI.RemoveItem>
                  </UI.ProductRow>
                )
              })}
            </UI.ProductList>
            <UI.Info>
              <UI.StickyInfoWrapper>
                <UI.TotalInfo>
                  <UI.TotalInfoTitle>Итого:</UI.TotalInfoTitle>
                  <strong>
                    <span data-any-total={basket.total}>{basket.total}</span>
                  </strong>
                </UI.TotalInfo>
                <UI.Actions>
                  {basket.haveNotAvailable && (
                    <Alert
                      type="warning"
                      description="В корзине имеются товары, которых нет в наличии. Пожалуйста, удалите такие товары из корзины чтобы продолжить."
                    />
                  )}

                  <UI.BuyButton disabled={basket.haveNotAvailable} onClick={goBuy}>
                    Оформить заказ
                  </UI.BuyButton>
                </UI.Actions>
              </UI.StickyInfoWrapper>
            </UI.Info>
          </>
        )) || <UI.BasketEmpty>Корзина пуста</UI.BasketEmpty>}
      </UI.BasketContent>

      {(props.content.cart_showcase ?? []).length > 0 && (
        <UI.ShowcaseProducts>
          <UI.ShowcaseProductsTitle>
            Товары для акции <i>(добавьте в корзину для изменения цены)</i>
          </UI.ShowcaseProductsTitle>
          <ProductsListContainer products={props.content.cart_showcase ?? []} />
        </UI.ShowcaseProducts>
      )}
    </>
  )
})
